<template>
    <wait v-if="h.loading" />
    <div v-else class="mx-auto max-w-2xl">
        <v-sheet class="!rounded-2xl !shadow-sm px-2 sm:px-3 pt-2 sm:pt-3 sm:py-1">
            <template v-if="h.receipt">
                <ReceiptDetail :receipt="h.receipt" :ride="true" />
            </template>
            <template v-else>
                <p class="text-center py-3">
                    {{ t('taxilib.receipt.not_found') }}
                </p>
            </template>
        </v-sheet>
        <div v-if="h.receipt" class="grid sm:grid-cols-2 gap-4 mt-4">
            <ActionCard
                v-if="h.receipt.ecr_status === 'receipt'"
                @click="h.receipt_dialog = true"
                :icon="Icons.TISearch"
                :title="t('taxilib.receipt.url_png')"
                :desc="t('taxilib.receipt.url_png_desc')"
            />
            <ActionCard
                v-if="h.receipt.ecr_status === 'receipt'"
                @click="open_jws()"
                :icon="Icons.TIDownload"
                :title="t('taxilib.receipt.url_jws')"
                :desc="t('taxilib.receipt.url_jws_desc')"
            />
        </div>
        <v-dialog width="auto" v-model="h.receipt_dialog">
            <v-sheet class="mx-auto !rounded-xl !shadow-md p-4 sm:p-6">
                <div class="my-6 px-12" v-if="!h.receipt_loaded">
                    <p class="mb-3 text-center">
                        {{ t('taxilib.receipt.loading_img') }}
                    </p>
                    <v-progress-linear indeterminate></v-progress-linear>
                </div>
                <div>
                    <v-img
                        class="max-w-96 mx-auto mb-4"
                        :src="`${api.url}/receipt/url/view/${props.id}/${props.secret}`"
                        @loadstart="h.receipt_loaded = false"
                        @load="h.receipt_loaded = true"
                    />
                </div>
                <div class="grid sm:grid-cols-2 gap-4" v-if="h.receipt_loaded">
                    <ActionCard
                        @click="open_png()"
                        :icon="Icons.TIDownload"
                        :title="t('taxilib.receipt.download_png')"
                        :desc="t('taxilib.receipt.download_png_desc')"
                    />
                    <ActionCard
                        @click="open_pdf()"
                        :icon="Icons.TIDownload"
                        :title="t('taxilib.receipt.url_pdf')"
                        :desc="t('taxilib.receipt.url_pdf_desc')"
                    />
                </div>
            </v-sheet>
        </v-dialog>
    </div>
</template>

<script setup>
    import { LocalDateTime } from '@js-joda/core';
    import { reactive, onMounted, inject, onBeforeUnmount } from 'vue';
    import { useI18n } from 'vue-i18n';
    import { ReceiptDetail, useAPI, log, Icons } from 'taxi-lib';
    import ActionCard from "@hq-receipt/component/ActionCard.vue";
    
    const { t } = useI18n();
    const { api }  = useAPI();
    const alert = inject('alert_plugin');

    const props = defineProps({
        id: {
            required: false,
            default: null,
        },
        secret: {
            type: String,
            required: false,
            default: null,
        },
    })

    const h = reactive({
        loading: true,
        receipt: null,
        receipt_loaded: false,
        receipt_dialog: false,
        ws: null,
    })

    function open_jws() {
        let url_jws = `${api.url}/receipt/url/download_jws/${props.id}/${props.secret}`;
        window.open(url_jws);
    }

    function open_png() {
        let url_png = `${api.url}/receipt/url/view/${props.id}/${props.secret}`;
        window.open(url_png, '_blank');
    }

    function open_pdf() {
        let url_pdf = `${api.url}/receipt/url/download/${props.id}/${props.secret}`;
        window.open(url_pdf);
    }

    async function load_receipt() {
        h.loading = true;
        let resp = await api.get(`/receipt/url/get/${props.id}/${props.secret}`);
        log.d("RESP", resp);

        let ok = await alert.api_alert(resp);
        h.loading = false;
        if (!ok) {
            return;
        }
        h.receipt = resp.data;
    }

    function connect_websocket() {
        let ws_url = `${api.url}/receipt/url/ws/${props.id}/${props.secret}`;
        h.ws = new WebSocket(ws_url);

        h.ws.onopen = () => {
            log.d('Receipt ws connected');
        };

        h.ws.onmessage = async (event) => {
            let data = JSON.parse(event.data);

            log.d('Receipt ws message', data);

            if (data.error) {
                log.d('Receipt ws error', data);
                return;
            }

            if (data.updated_at && h.receipt?.updated_at) {
                const ws_updated_at = LocalDateTime.parse(data.updated_at);
                const receipt_updated_at = LocalDateTime.parse(h.receipt.updated_at);

                if (ws_updated_at.isAfter(receipt_updated_at)) {
                    await load_receipt();
                }
            }
        };

        h.ws.onerror = (error) => {
            log.d('Receipt ws error', error);
        };

        h.ws.onclose = () => {
            log.d('Receipt ws closed');
            h.ws = null;
        };
    }

    onMounted(async () => {
        if (props.id === null) {
            h.loading = false;
            return;
        }
        await load_receipt();
        if(h.receipt) {
            connect_websocket();
        }
    })

    onBeforeUnmount(() => {
        if (h.ws) {
            h.ws.close();
            h.ws = null;
        }
    });
</script>